import React, { useState, useCallback, useContext, useMemo } from 'react';
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Checkbox from '@mui/material/Checkbox';
import { visuallyHidden } from '@mui/utils';

import './ActivitiesList.scss';
import { IActivity, ILocation } from 'src/types';
import { ImpactLabel } from '../ImpactLabel';
import classNames from 'classnames';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import EditIcon from '@mui/icons-material/Edit';
import { AlertDialog } from '../AlertDialog';
import { ActivitiesDialog } from '../ActivitiesDialog';
import { EnhancedSearch } from '../EnhacedSearch';

const className = 'administration-activities';

type Order = 'asc' | 'desc';

interface IImpacts {
  isWater: boolean;
  isEnergy: boolean;
  isWaste: boolean;
  isEmissions: boolean;
  isInclusion: boolean;
  isCommunity: boolean;
}

export interface IRowsActivities extends IActivity {
  impacts: IImpacts;
}

function createData(res): Partial<IRowsActivities> {
  const {
    activityId,
    description,
    positiveAction,
    isWater,
    isEnergy,
    isWaste,
    isEmissions,
    isInclusion,
    isCommunity,
    isHidden,
  } = res;
  const impacts = { isWater, isEnergy, isWaste, isEmissions, isInclusion, isCommunity };
  return { activityId, description, positiveAction, impacts, isHidden };
}

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key,
): (a: { [key in Key]: number | string }, b: { [key in Key]: number | string }) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(array: readonly T[], comparator: (a: T, b: T) => number) {
  return array.slice().sort(comparator);
}

interface HeadCell {
  disablePadding: boolean;
  id: keyof IActivity;
  label: string;
  numeric: boolean;
}

const headCells: readonly HeadCell[] = [
  {
    id: 'positiveAction',
    numeric: false,
    disablePadding: true,
    label: 'Positive action',
  },
];

interface EnhancedTableProps {
  numSelected: number;
  onRequestSort: (event: React.MouseEvent<unknown>, property: keyof IActivity) => void;
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  order: Order;
  orderBy: string;
  rowCount: number;
}

function EnhancedTableHead(props: EnhancedTableProps) {
  const { onSelectAllClick, order, orderBy, numSelected, rowCount, onRequestSort } = props;
  const createSortHandler = (property: keyof IActivity) => (event: React.MouseEvent<unknown>) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox">
          <Checkbox
            color="primary"
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={rowCount > 0 && numSelected === rowCount}
            onChange={onSelectAllClick}
            inputProps={{
              'aria-label': 'select all desserts',
            }}
          />
        </TableCell>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? 'right' : 'left'}
            padding={headCell.disablePadding ? 'none' : 'normal'}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

interface EnhancedTableToolbarProps {
  numSelected: number;
  selected: readonly number[];
  rowData: IActivity;
  locationsList: ILocation[];
  handleAdd: (...args: any[]) => any;
  handleUpdate: (...args: any[]) => any;
  handleDelete: (...args: any[]) => any;
  handleHide: (...args: any[]) => any;
  handleUnhide: (...args: any[]) => any;
}

function EnhancedTableToolbar(props: EnhancedTableToolbarProps) {
  const {
    numSelected,
    selected,
    locationsList,
    handleAdd,
    handleUpdate,
    handleDelete,
    handleHide,
    handleUnhide,
    rowData,
  } = props;
  const [openDeleteAlert, setOpenDeleteAlert] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const handleCloseDialog = () => setOpenDialog(false);
  const handleOpenAlert = () => setOpenDeleteAlert(true);
  const handleCloseAlert = () => setOpenDeleteAlert(false);
  const [handleSave, setHandleSave] = useState(() => handleAdd);
  const [selectedData, setSelectedData] = useState(null);

  const handleOpenDialog = (action) => {
    let saveFunction;
    if (action !== 'edit') {
      saveFunction = handleAdd;
      setSelectedData(null);
    } else {
      saveFunction = handleUpdate;
      setSelectedData(rowData);
    }
    setHandleSave(() => saveFunction);
    setOpenDialog(true);
  };

  const handleAgree = () => {
    handleDelete(selected);
  };

  const handleDisagree = () => {
    console.log('User disagree');
  };

  return (
    <Toolbar
      sx={{
        py: 1,
        pl: { sm: 2 },
        pr: { xs: 1, sm: 1 },
        ...(numSelected > 0 && {
          bgcolor: (theme) => alpha(theme.palette.primary.main, theme.palette.action.activatedOpacity),
        }),
      }}
    >
      <AlertDialog
        open={openDeleteAlert}
        handleClose={handleCloseAlert}
        onAgree={handleAgree}
        onDisagree={handleDisagree}
      />

      <ActivitiesDialog
        open={openDialog}
        formData={selectedData}
        locationsList={locationsList}
        handleClose={handleCloseDialog}
        onSave={handleSave}
      />

      {numSelected > 0 ? (
        <Box>
          <Box sx={{ mb: 1 }}>
            <Button startIcon={<AddIcon />} sx={{ textTransform: 'none' }} onClick={handleOpenDialog}>
              Add new
            </Button>
            {numSelected > 0 && numSelected < 2 ? (
              <>
                <Button
                  startIcon={<EditIcon />}
                  sx={{ textTransform: 'none' }}
                  onClick={() => handleOpenDialog('edit')}
                >
                  Edit
                </Button>
                <Button startIcon={<DeleteIcon />} sx={{ textTransform: 'none' }} onClick={handleOpenAlert}>
                  Delete
                </Button>
              </>
            ) : (
              ''
            )}
            <Button
              startIcon={<VisibilityOffIcon />}
              sx={{ textTransform: 'none' }}
              onClick={() => handleHide(selected)}
            >
              Hide from site
            </Button>
            <Button
              startIcon={<VisibilityIcon />}
              sx={{ textTransform: 'none' }}
              onClick={() => handleUnhide(selected)}
            >
              Show on site
            </Button>
          </Box>
          <Typography sx={{ flex: '1 1 100%' }} color="inherit" variant="subtitle2" component="div">
            {numSelected} selected
          </Typography>
        </Box>
      ) : (
        <Button startIcon={<AddIcon />} sx={{ textTransform: 'none' }} onClick={handleOpenDialog}>
          Add new
        </Button>
      )}
    </Toolbar>
  );
}

interface ActivitiesListProps {
  listData: Partial<IActivity[]>;
  locationsList: ILocation[];
  handleAdd: (...args: any[]) => any;
  handleUpdate: (...args: any[]) => any;
  handleDelete: (...args: any[]) => any;
  handleHide: (...args: any[]) => any;
  handleUnhide: (...args: any[]) => any;
}

export function ActivitiesList(props: ActivitiesListProps) {
  const { listData, locationsList, handleAdd, handleUpdate, handleDelete, handleHide, handleUnhide } = props;
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<keyof IRowsActivities>('positiveAction');
  const [selected, setSelected] = useState<readonly number[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const rows: any = listData.map((item) => createData(item));
  const [filteredRows, setFilteredRows] = useState(rows);

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof IRowsActivities) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = rows.map((n) => n.activityId);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event: React.MouseEvent<unknown>, id: any) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected: readonly number[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
    }
    setSelected(newSelected);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = (data) => {
    setFilteredRows(data);
  };

  const isSelected = (id: any) => selected.indexOf(id) !== -1;
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  const visibleRows = useMemo(
    () =>
      stableSort(filteredRows, getComparator(order, orderBy)).slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage,
      ),
    [order, orderBy, page, rowsPerPage, filteredRows],
  );

  return (
    <Box sx={{ width: '100%' }}>
      <Paper sx={{ width: '100%', mb: 2 }}>
        <EnhancedTableToolbar
          selected={selected}
          rowData={listData.find((item) => item.activityId === selected[0])}
          locationsList={locationsList}
          numSelected={selected.length}
          handleAdd={handleAdd}
          handleUpdate={handleUpdate}
          handleDelete={handleDelete}
          handleHide={handleHide}
          handleUnhide={handleUnhide}
        />
        <EnhancedSearch rows={rows} filterRows={handleSearch} />
        <TableContainer>
          <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle">
            <EnhancedTableHead
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={rows.length}
            />
            <TableBody>
              {visibleRows.map((row, index) => {
                const isItemSelected = isSelected(row.activityId);
                const labelId = `enhanced-table-checkbox-${index}`;

                return (
                  <TableRow
                    hover
                    onClick={(event) => handleClick(event, row.activityId)}
                    role="checkbox"
                    aria-checked={isItemSelected}
                    tabIndex={-1}
                    key={row.activityId}
                    selected={isItemSelected}
                    sx={{ cursor: 'pointer' }}
                    className={row.isHidden ? `${className}__table-row-hidden` : ''}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        color="primary"
                        checked={isItemSelected}
                        inputProps={{
                          'aria-labelledby': labelId,
                        }}
                      />
                    </TableCell>
                    <TableCell component="th" id={labelId} scope="row" padding="none">
                      <div className={classNames(`${className}__table-cell`)}>
                        {row.positiveAction}
                        <ImpactLabel impactsList={row.impacts} />
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
              {emptyRows > 0 && (
                <TableRow>
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[{ label: 'All', value: -1 }, 25, 50, 100]}
          component="div"
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </Box>
  );
}
